import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { KlDetailFilterComponent } from "@app/modules/shared/components/kl-detail-filter/kl-detail-filter.component";
import { BehaviorSubject, forkJoin, of, Subscription } from "rxjs";
import { findIndex, isEmpty } from "lodash";
import { PrimengLocal } from "@app/modules/shared/constants/primeng-local";
import { FilterItem } from "@app/modules/shared/model/detail-filter.interface";
import {
  ExplorerService,
  ModalConfig,
  TransferState,
} from "@app/modules/shared/services/explorer.service";
import { KlSweetAlertService } from "@app/modules/shared/services/message/kl-sweet-alert.service";
import { KlLoadingService } from "@app/modules/shared/services/kl-loading.service";
import { UtilService } from "@app/modules/shared/services/util.service";
import { ConvertIsoDateService } from "@app/modules/shared/services/convert-iso-date.service";
import { TranslateService } from "@app/modules/shared/services/translate/translate.service";
import { ApiService } from "@app/modules/shared/services/base/api.service";
import { DynamicComponent } from "@app/data/dashboard/interfaces/dynamic-component.interface";
import { CustomerListComponent } from "@dashboard/modules/shipping-management/customer/components/customer-list/customer-list.component";
import { catchError, map } from "rxjs/operators";

@Component({
  selector: "kl-customer-detail-filter",
  templateUrl: "./customer-detail-filter.component.html",
  styleUrls: ["./customer-detail-filter.component.scss"],
})
export class CustomerDetailFilterComponent implements OnInit, OnDestroy {
  @Input() entity;
  @Input() showSavedFilters = true;
  @Input() showBottomButtons = true;
  @ViewChild(forwardRef(() => KlDetailFilterComponent), {
    static: true,
  })
  klDetailFilterComponent: any;
  forkJoinStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  locale =
    PrimengLocal[
      localStorage.getItem("currentLanguage")
        ? localStorage.getItem("currentLanguage").toUpperCase()
        : "TR"
    ];
  tunnel: Subscription;
  entryName: string;
  currentFilter: any;
  saveForm = false;
  selectedFilterValues: Array<FilterItem> = [];
  filterList = [];
  codeExpressionValue: any;
  descriptionExpressionValue: any;
  identityNumExpressionValue: any;

  alertShow = false;
  alertMessage = "";

  private _statusValue: any;
  set statusValue(value: any) {
    this._statusValue = value;
    if (value) {
      const payload = {
        label: "status",
        key: "statusValue",
        value: value,
        accessor: "status",
      };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "status" },
        payload
      );
    } else {
      this._statusValue = null;
      const index = findIndex(this.selectedFilterValues, { label: "status" });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get statusValue() {
    return this._statusValue;
  }

  private _codeValue: any;
  set codeValue(value: any) {
    if (value) {
      this._codeValue = value;
      const payload = { label: "code", key: "codeValue", value: value };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "code" },
        payload
      );
    } else {
      const index = findIndex(this.selectedFilterValues, { label: "code" });
      this._codeValue = null;
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get codeValue() {
    return this._codeValue;
  }

  private _descriptionValue: any;
  set descriptionValue(value: any) {
    if (value) {
      this._descriptionValue = value;
      const payload = {
        label: "description",
        key: "descriptionValue",
        value: value,
      };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "description" },
        payload
      );
    } else {
      this._descriptionValue = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "description",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get descriptionValue() {
    return this._descriptionValue;
  }

  private _identityNumValue: any;
  set identityNumValue(value: any) {
    if (value) {
      this._identityNumValue = value;
      const payload = {
        label: "identity_num",
        key: "identityNumValue",
        value: value,
      };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "identity_num" },
        payload
      );
    } else {
      this._identityNumValue = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "identity_num",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get identityNumValue() {
    return this._identityNumValue;
  }

  private _userValue: any;
  set userValue(value: any) {
    if (value) {
      this._userValue = value;
      const payload = {
        label: "user",
        key: "userValue",
        value: value,
        accessor: "username",
      };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "user" },
        payload
      );
    } else {
      this._userValue = null;
      const index = findIndex(this.selectedFilterValues, { label: "user" });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get userValue() {
    return this._userValue;
  }

  private _lastChangedUserValue: any;
  set lastChangedUserValue(value: any) {
    if (value) {
      this._lastChangedUserValue = value;
      const payload = {
        label: "last_changed_user",
        key: "lastChangedUserValue",
        value: value,
        accessor: "username",
      };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "last_changed_user" },
        payload
      );
    } else {
      this._lastChangedUserValue = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "last_changed_user",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get lastChangedUserValue() {
    return this._lastChangedUserValue;
  }

  private _firstDate: Date;
  set firstDate(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._firstDate = new Date(
          this.dateConvert.convertDate(value) + " 00:00:00"
        );
      } else if (typeof value === "string") {
        this._firstDate = new Date(value);
      }
      const payload = {
        label: "first_record_date",
        key: "firstDate",
        value: this._firstDate,
      };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "first_record_date" },
        payload
      );
    } else {
      this._firstDate = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "first_record_date",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get firstDate() {
    return this._firstDate;
  }

  private _lastDate: Date;
  set lastDate(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._lastDate = new Date(
          this.dateConvert.convertDate(value) + " 23:59:59"
        );
      } else if (typeof value === "string") {
        this._lastDate = new Date(value);
      }
      const payload = {
        label: "last_record_date",
        key: "lastDate",
        value: this._lastDate,
      };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "last_record_date" },
        payload
      );
    } else {
      this._lastDate = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "last_record_date",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get lastDate() {
    return this._lastDate;
  }

  private _firstDateLastChanged: Date;
  set firstDateLastChanged(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._firstDateLastChanged = new Date(
          this.dateConvert.convertDate(value) + " 00:00:00"
        );
      } else if (typeof value === "string") {
        this._firstDateLastChanged = new Date(value);
      }
      const payload = {
        label: "first_date_changed",
        key: "firstDateLastChanged",
        value: this._firstDateLastChanged,
      };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "first_date_changed" },
        payload
      );
    } else {
      this._firstDateLastChanged = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "first_date_changed",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get firstDateLastChanged() {
    return this._firstDateLastChanged;
  }

  private _lastDateLastChanged: Date;
  set lastDateLastChanged(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._lastDateLastChanged = new Date(
          this.dateConvert.convertDate(value) + " 23:59:59"
        );
      } else if (typeof value === "string") {
        this._lastDateLastChanged = new Date(value);
      }
      const payload = {
        label: "last_date_changed",
        key: "lastDateLastChanged",
        value: this._lastDateLastChanged,
      };
      CustomerDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "last_date_changed" },
        payload
      );
    } else {
      this._lastDateLastChanged = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "last_date_changed",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get lastDateLastChanged() {
    return this._lastDateLastChanged;
  }

  dateFormatString: string;
  userOptions: any;
  CustomerComponent = CustomerListComponent;
  statusOptions = [
    {
      label: this.translateService.instant("ALL"),
      value: null,
    },
    {
      label: this.translateService.instant("ACTIVE"),
      value: {
        status: "ACTIVE",
      },
    },
    {
      label: this.translateService.instant("PASSIVE"),
      value: {
        status: "PASSIVE",
      },
    },
  ];
  expressionOptions = [
    {
      label: this.translateService.instant("LIKE"),
      value: "LIKE",
    },
    {
      label: this.translateService.instant("NOT_LIKE"),
      value: "NOT_LIKE",
    },
    {
      label: this.translateService.instant("EQ"),
      value: "EQ",
    },
    {
      label: this.translateService.instant("NOT_EQ"),
      value: "NOT_EQ",
    },
    // {
    //   label: this.translateService.instant('IN'),
    //   value: 'IN'
    // },
    // {
    //   label: this.translateService.instant('NOT_IN'),
    //   value: 'NOT_IN'
    // },
    {
      label: this.translateService.instant("START_WITH"),
      value: "START_WITH",
    },
    {
      label: this.translateService.instant("END_WITH"),
      value: "END_WITH",
    },
    {
      label: this.translateService.instant("GT"),
      value: "GT",
    },
    {
      label: this.translateService.instant("GE"),
      value: "GE",
    },
    {
      label: this.translateService.instant("LT"),
      value: "LT",
    },
    {
      label: this.translateService.instant("LE"),
      value: "LE",
    },
  ];
  productGroupTypes = [
    {
      label: this.translateService.instant("J_RETAIL"),
      value: {
        type: "J_RETAIL",
      },
    },
    {
      label: this.translateService.instant("KL_RETAIL"),
      value: {
        type: "KL_RETAIL",
      },
    },
    {
      label: this.translateService.instant("VENUT"),
      value: {
        type: "VENUT",
      },
    },
  ];
  packageTypes = [
    {
      label: this.translateService.instant("MAIN_PACKAGE"),
      value: {
        type: "MAIN_PACKAGE",
      },
    },
    {
      label: this.translateService.instant("PLUS_USER"),
      value: {
        type: "PLUS_USER",
      },
    },
    {
      label: this.translateService.instant("ADDITION_PACKAGE"),
      value: {
        type: "ADDITION_PACKAGE",
      },
    },
  ];

  static insertOrUpdate(array: any[], meta: any, value: any) {
    const index = findIndex(array, meta);
    if (index >= 0) {
      array.splice(index, 1);
      array.push(value);
      return;
    }
    return array.push(value);
  }

  constructor(
    private explorerService: ExplorerService,
    private alertService: KlSweetAlertService,
    private klLoadingService: KlLoadingService,
    private utilService: UtilService,
    private dateConvert: ConvertIsoDateService,
    private translateService: TranslateService,
    private apiService: ApiService
  ) {
    this.dateFormatString = UtilService.dateFormatString
      .toLowerCase()
      .replace("yy", "");
    this.tunnel = this.explorerService.transferState$.subscribe((state) => {
      switch (state.state) {
        case "MATERIAL_DETAIL_FILTER_MATERIAL_CODE_CURRENT_ITEM":
          this.codeValue = state.payload.code;
          break;
        case "MATERIAL_DETAIL_FILTER_MATERIAL_DESCRIPTION_CURRENT_ITEM":
          this.descriptionValue = state.payload.description;
          break;
      }
    });
  }

  ngOnInit() {
    this.codeExpressionValue = "LIKE";
    this.descriptionExpressionValue = "LIKE";
    this.identityNumExpressionValue = "LIKE";
    this.klLoadingService.show("loading");
    let commitObject: any;
    commitObject = {
      parent: {
        module: this.entity.parentModule,
      },
      module: this.entity.module,
      key: this.entity.state,
    };
    const preference = this.apiService
      .getFilteredData("design", commitObject)
      .pipe(
        map((response: any) => response),
        catchError((error) => this.handleError(error))
      );
    const users = this.apiService.getAllData("user").pipe(
      map((response: any) => response),
      catchError((error) => this.handleError(error))
    );
    forkJoin([preference, users])
      .finally(() => {
        this.klLoadingService.hide();
        if (this.entity.filter) {
          this.selectFilter({
            item: { content: JSON.stringify(this.entity.filter) },
            select: false,
          });
        }
      })
      .subscribe(
        ([preferenceResponse, userResponse]) => {
          this.userOptions = userResponse;
          this.filterList = preferenceResponse.data;
          if (!this.entity.filter) {
            this.filterList.forEach((filter) => {
              if (filter.level_default === "DEFAULT") {
                this.selectFilter({ item: filter, select: true });
              }
            });
          }
          this.forkJoinStatus.next(true);
        },
        (error) => this.handleError(error)
      );
  }

  getFilters() {
    let commitObject: any;
    commitObject = {
      parent: {
        module: this.entity.parentModule,
      },
      module: this.entity.module,
      key: this.entity.state,
    };
    this.klLoadingService.show("loading");
    this.apiService.getFilteredData("design", commitObject).subscribe(
      (resPreference) => {
        this.klLoadingService.hide();
        this.filterList = resPreference.data;
      },
      (errPreference) => this.handleError(errPreference)
    );
  }

  selectFilter(event) {
    this.clearAll();
    if (event.select) {
      this.currentFilter = event.item;
    }
    let selectObject: any;
    selectObject = JSON.parse(event.item.content);
    const filterObject = selectObject["filter"];
    const paramsObject = selectObject["parameters"];
    this.selectedFilterValues = [];
    const parametersObject = [];
    if (filterObject.hasOwnProperty("status")) {
      this.statusValue = {
        status: filterObject["status"],
      };
    }
    if (filterObject.hasOwnProperty("code")) {
      this.codeValue = filterObject["code"];
      paramsObject.forEach((params) => {
        if (params.name === "code") {
          this.codeExpressionValue = params.expression["operator"];
        }
      });
      parametersObject.push({
        name: "code",
        expression: {
          operator: this.codeExpressionValue,
        },
      });
    }
    if (filterObject.hasOwnProperty("description")) {
      this.descriptionValue = filterObject["description"];
      paramsObject.forEach((params) => {
        if (params.name === "description") {
          this.descriptionExpressionValue = params.expression["operator"];
        }
      });
      parametersObject.push({
        name: "description",
        expression: {
          operator: this.descriptionExpressionValue,
        },
      });
    }
    if (filterObject.hasOwnProperty("identity_num")) {
      this.identityNumValue = filterObject["identity_num"];
      paramsObject.forEach((params) => {
        if (params.name === "identity_num") {
          this.identityNumExpressionValue = params.expression["operator"];
        }
      });
      parametersObject.push({
        name: "identity_num",
        expression: {
          operator: this.identityNumExpressionValue,
        },
      });
    }
    if (filterObject.hasOwnProperty("modified_user")) {
      this.lastChangedUserValue = this.userOptions.find(
        (user) => user.username === filterObject["modified_user"].username
      );
    }
    if (filterObject.hasOwnProperty("created_user")) {
      this.userValue = this.userOptions.find(
        (user) => user.username === filterObject["created_user"].username
      );
    }
    if (filterObject.hasOwnProperty("created_dates")) {
      if (filterObject["created_dates"]["min"]) {
        this.firstDate = this.dateConvert.utcDate(
          new Date(filterObject["created_dates"]["min"])
        );
      }
      if (filterObject["created_dates"]["max"]) {
        this.lastDate = this.dateConvert.utcDate(
          new Date(filterObject["created_dates"]["max"])
        );
      }
    }
    if (filterObject.hasOwnProperty("modified_dates")) {
      if (filterObject["modified_dates"]["min"]) {
        this.firstDateLastChanged = this.dateConvert.utcDate(
          new Date(filterObject["modified_dates"]["min"])
        );
      }
      if (filterObject["modified_dates"]["max"]) {
        this.lastDateLastChanged = this.dateConvert.utcDate(
          new Date(filterObject["modified_dates"]["max"])
        );
      }
    }
  }

  setModal(
    title: string,
    stateSelector: string,
    template: any,
    haveFilter = true,
    modalId
  ) {
    const modalConfig = <ModalConfig>{
      title: title,
      stateSelector: "MATERIAL_DETAIL_FILTER_" + stateSelector,
      childTemplate: <DynamicComponent>{
        mode: "DIALOG",
        modalId: modalId,
        dblSelect: true,
        template: template,
        templateContext: {
          filter: haveFilter ? { type: "MATERIAL_CARD" } : {},
        },
      },
    };
    this.explorerService.openModal(modalConfig);
  }

  deleteFromValues(event) {
    this.selectedFilterValues.splice(event.index, 1);
    this[event.item.key] = null;
  }

  deleteSavedFilter(event) {
    this.alertService.alertDelete(
      {
        title: "Uyarı",
        text: "Kayıt Silinecektir",
        customClass: "kl-alert",
        showCloseButton: false,
      },
      () => {
        this.klLoadingService.show("deleting");
        this.apiService.deleteData("design", event.item.id).subscribe(
          () => {
            this.clearAll();
            this.klLoadingService.hide();
            this.filterList.splice(event.index, 1);
          },
          (errDeletePreference) => this.handleError(errDeletePreference)
        );
      }
    );
  }

  clearAll() {
    delete this.currentFilter;
    for (let i = 0; i < this.selectedFilterValues.length; i++) {
      this[this.selectedFilterValues[i].key] = null;
      i = i - 1;
    }
    this.selectedFilterValues = [];
  }

  commitSave(event: any) {
    this.saveForm = true;
    let commitObject: any;
    commitObject = {
      parent: {
        module: this.entity.parentModule,
      },
      level: event.levelStatus ? "APPLICATION" : "USER",
      level_default: event.levelDesign ? "DEFAULT" : "NO_DEFAULT",
      description: event.entryName,
      module: this.entity.module,
      type: "FILTER",
      key: this.entity.state,
      content: JSON.stringify(this.prepareFilterObject()),
    };
    switch (event.commitType) {
      case "add":
        this.apiService.addData("design", commitObject).subscribe(
          (resPreference) => {
            this.saveForm = false;
            this.getFilters();
            this.selectFilter({ item: resPreference, select: true });
            this.klDetailFilterComponent.closeEntryModal();
          },
          (errPreference) => {
            this.saveForm = false;
            this.alertService.alertError({ title: "error" }, errPreference);
          }
        );
        break;
      case "edit":
        commitObject["id"] = this.currentFilter.id;
        this.apiService.updateData("design", commitObject).subscribe(
          (resPreference) => {
            this.saveForm = false;
            this.getFilters();
            this.selectFilter({ item: resPreference, select: true });
            this.klDetailFilterComponent.closeEntryModal();
          },
          (errPreference) => {
            this.saveForm = false;
            this.alertService.alertError({ title: "error" }, errPreference);
          }
        );
        break;
    }
  }

  commitFilter() {
    if (
      this.firstDate > this.lastDate ||
      this.firstDateLastChanged > this.lastDateLastChanged
    ) {
      this.alertMessage = this.translateService.instant(
        "end_date_cannot_be_less_than_start_date"
      );
      this.alertShow = true;
    } else {
      const preparedFilter: any = this.prepareFilterObject();
      if (preparedFilter.hasOwnProperty("close")) {
        delete preparedFilter.close;
      }
      if (isEmpty(preparedFilter["filter"]["modified_dates"])) {
        delete preparedFilter["filter"]["modified_dates"];
      }
      if (isEmpty(preparedFilter["filter"]["created_dates"])) {
        delete preparedFilter["filter"]["created_dates"];
      }
      const state = <TransferState>{
        state: this.entity.state,
        payload: preparedFilter,
      };
      this.explorerService.transferState(state);
      this.explorerService.closeModal(this);
    }
  }

  closeFilter() {
    const preparedFilter = this.prepareFilterObject();
    Object.assign(preparedFilter, { close: true });
    this.explorerService.transferState({
      state: this.entity.state + "_DETAIL_FILTER",
      payload: preparedFilter,
    });
    this.explorerService.closeModal(this);
  }

  prepareFilterObject() {
    const filterObject = Object.create(null);
    filterObject["modified_dates"] = Object.create(null);
    filterObject["created_dates"] = Object.create(null);
    const parametersObject = [];
    if (this.statusValue) {
      filterObject["status"] = this.statusValue.status;
    }
    if (this.codeValue) {
      if (this.codeExpressionValue === "IN") {
        if (this.codeValue.includes(";")) {
          this.codeValue = this.codeValue.replace(" ", "");
          filterObject["code"] = this.codeValue.split(";");
        } else {
          filterObject["code"] = [this.codeValue];
        }
      } else {
        filterObject["code"] = this.codeValue;
      }
      parametersObject.push({
        name: "code",
        expression: {
          operator: this.codeExpressionValue,
        },
      });
    }
    if (this.descriptionValue) {
      filterObject["description"] = this.descriptionValue;
      parametersObject.push({
        name: "description",
        expression: {
          operator: this.descriptionExpressionValue,
        },
      });
    }
    if (this.identityNumValue) {
      filterObject["identity_num"] = this.identityNumValue;
      parametersObject.push({
        name: "identity_num",
        expression: {
          operator: this.identityNumExpressionValue,
        },
      });
    }
    if (this.userValue) {
      filterObject["created_user"] = { username: this.userValue.username };
    }
    if (this.lastChangedUserValue) {
      filterObject["modified_user"] = {
        username: this.lastChangedUserValue.username,
      };
    }
    if (this.firstDate) {
      const date = this.dateConvert.convertDate(this.firstDate);
      const newDate = new Date(date + "T00:00:01.000Z");
      filterObject["created_dates"]["min"] = newDate.getTime();
      parametersObject.push({
        name: "created_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    if (this.lastDate) {
      const date = this.dateConvert.convertDate(this.lastDate);
      const newDate = new Date(date + "T23:59:59.000Z");
      filterObject["created_dates"]["max"] = newDate.getTime();
      parametersObject.push({
        name: "created_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    if (this.firstDateLastChanged) {
      const date = this.dateConvert.convertDate(this.firstDateLastChanged);
      const newDate = new Date(date + "T00:00:01.000Z");
      filterObject["modified_dates"]["min"] = newDate.getTime();
      parametersObject.push({
        name: "modified_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    if (this.lastDateLastChanged) {
      const date = this.dateConvert.convertDate(this.lastDateLastChanged);
      const newDate = new Date(date + "T23:59:59.000Z");
      filterObject["modified_dates"]["max"] = newDate.getTime();
      parametersObject.push({
        name: "modified_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    const query = {
      paging: {
        limit: this.entity.pageSize || 10,
        offset: 0,
        fetch: "EAGER",
      },
    };
    return Object.assign(
      {},
      { filter: filterObject },
      { parameters: parametersObject },
      { query: query }
    );
  }

  handleError(error) {
    this.alertMessage = this.utilService.alertErrorMessage(error);
    this.alertShow = true;
    this.klLoadingService.hide();
    return of();
  }

  ngOnDestroy() {
    this.tunnel.unsubscribe();
  }
}
